import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Search } from "lucide-react";

interface GlossaryTerm {
  term: string;
  definition: string;
  synonyms?: string[];
}

interface GlossaryViewProps {
  terms: GlossaryTerm[];
}

export const GlossaryView = ({ terms }: GlossaryViewProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [openAccordion, setOpenAccordion] = useState<string | undefined>();

  // Filter and sort terms
  const filteredTerms = useMemo(() => {
    let filtered = terms;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (term) =>
          term.term.toLowerCase().includes(query) ||
          term.definition.toLowerCase().includes(query) ||
          term.synonyms?.some((syn) => syn.toLowerCase().includes(query))
      );
    }

    // Apply letter filter
    if (selectedLetter) {
      filtered = filtered.filter((term) =>
        term.term.toUpperCase().startsWith(selectedLetter)
      );
    }

    // Sort alphabetically
    return filtered.sort((a, b) => a.term.localeCompare(b.term, "nb"));
  }, [terms, searchQuery, selectedLetter]);

  // Get available letters
  const availableLetters = useMemo(() => {
    const letters = new Set<string>();
    terms.forEach((term) => {
      const firstLetter = term.term.charAt(0).toUpperCase();
      if (firstLetter.match(/[A-ZÆØÅ]/)) {
        letters.add(firstLetter);
      }
    });
    return Array.from(letters).sort((a, b) => a.localeCompare(b, "nb"));
  }, [terms]);

  // Handle synonym click
  const handleSynonymClick = (synonym: string) => {
    const termIndex = filteredTerms.findIndex(
      (t) => t.term.toLowerCase() === synonym.toLowerCase()
    );

    if (termIndex !== -1) {
      const termId = `term-${termIndex}`;
      setOpenAccordion(termId);

      setTimeout(() => {
        const element = document.getElementById(termId);
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Begriffe suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Alphabetical Navigation */}
        <div className="flex flex-wrap gap-1 mt-4">
          <button
            onClick={() => setSelectedLetter(null)}
            className={`px-2 py-1 text-xs font-medium rounded-sm transition-colors ${
              selectedLetter === null
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            }`}
          >
            Alle
          </button>
          {availableLetters.map((letter) => (
            <button
              key={letter}
              onClick={() => setSelectedLetter(letter)}
              className={`px-2 py-1 text-xs font-medium rounded-sm transition-colors ${
                selectedLetter === letter
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
              }`}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      {/* Term Count */}
      <div className="text-sm text-muted-foreground">
        Viser {filteredTerms.length} av {terms.length} begreper
      </div>

      {/* Glossary Terms Accordion */}
      {filteredTerms.length > 0 ? (
        <Accordion 
          type="single" 
          collapsible 
          className="space-y-2"
          value={openAccordion}
          onValueChange={setOpenAccordion}
        >
          {filteredTerms.map((term, index) => (
            <AccordionItem
              key={`${term.term}-${index}`}
              value={`term-${index}`}
              id={`term-${index}`}
              className="border rounded-lg px-4 bg-card"
            >
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-left">{term.term}</span>
                  {term.synonyms && term.synonyms.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {term.synonyms.length} synonym{term.synonyms.length > 1 ? "er" : ""}
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4 pb-2">
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: term.definition }}
                />
                {term.synonyms && term.synonyms.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Synonymer:</p>
                    <div className="flex flex-wrap gap-2">
                      {term.synonyms.map((synonym, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="cursor-pointer hover:bg-secondary/80 transition-colors"
                          onClick={() => handleSynonymClick(synonym)}
                        >
                          {synonym}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <p>Keine Begriffe gefunden.</p>
          <p className="text-sm mt-2">Versuchen Sie eine andere Suche oder entfernen Sie die Filter.</p>
        </div>
      )}
    </div>
  );
};
